import { createContext, useEffect, useReducer, ReactElement, useState, useContext } from 'react';

// third-party
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { getDownloadURL, uploadBytesResumable, ref, getStorage } from 'firebase/storage';

// action - state management
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';

// project-imports
//@ts-ignore
import Loader from 'components/Loader';
import { AuthProps, FirebaseContextType } from 'types/auth';
import serverCall, { Token } from 'api/serverCall';
import api from 'api/api';
import { FORM_VALIDATION_MESSAGES } from 'constants/messages';

let storage: any = null;
// firebase initialize
if (!firebase.apps.length) {
  const app = firebase.initializeApp({
    apiKey: 'AIzaSyCemwQ06LbVDBWaMldbh_CHv4s-UjaDjnc',

    authDomain: 'vealth1st-bd956.firebaseapp.com',

    projectId: 'vealth1st-bd956',

    storageBucket: 'vealth1st-bd956.appspot.com',

    messagingSenderId: '112988558059',

    appId: '1:112988558059:web:2e7b0db267b295adca24ae',

    measurementId: 'G-DL348FRPVT'
  });
  storage = getStorage(app);
}

// const
const initialState: AuthProps = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

export const getFirebaseErrorMessage = (error: any): string => {
  switch (error.code) {
    case 'auth/user-not-found':
      return 'User not found. Please check your email or sign up.';
    case 'auth/wrong-password':
      return 'Invalid password. Please try again.';
    case 'auth/email-already-in-use':
      return 'The email address is already in use by another account.';
    case 'auth/weak-password':
      return 'The password is too weak. Please choose a stronger password.';
    case 'auth/invalid-email':
      return 'Invalid email address. Please enter a valid email.';
    case 'auth/invalid-login-credentials':
      return 'Invalid login credentials. Please check your email and password.';
    case 'auth/user-disabled':
      return 'Unable to access account. Please contact administrator.';
    default:
      return FORM_VALIDATION_MESSAGES.SOMETHING_WENT_WRONG;
  }
};

// ==============================|| FIREBASE CONTEXT & PROVIDER ||============================== //

const FirebaseContext = createContext<FirebaseContextType | null>(null);

export const FirebaseProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [authServerError, setAuthServerError] = useState<any>(null);



  useEffect(() => {
    setAuthServerError(null);
    firebase.auth().onAuthStateChanged((user: any) => {
      if (user) {
        try {
          user.getIdToken().then((idToken: string) => {
            setAuthServerError(null);
            getUserFromServer(idToken);
          });
        } catch (error) {
          console.error(error);
        }
      } else {
        dispatch({
          type: LOGOUT
        });
      }
    });
  }, [dispatch]);

  function getUserFromServer(idToken: string) {
    Token.token = idToken;
    serverCall.get(api.login(), (response, error) => {
      if (error) {
        logout();
        setAuthServerError(error.response?.data);
      } else {
        setAuthServerError(null);
        if (response.password_expired) {
          setAuthServerError({ password_expired: true, message: 'Password has expired. Please reset your password.', userId: response.admin?._id });
        } else {
          const user = {
            ...response.admin,
            permissions: response.permissions
          };
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: user
            }
          });
        }
      }
    });
  }

  const firebaseEmailPasswordSignIn = (email: string, password: string) => firebase.auth().signInWithEmailAndPassword(email, password);

  const firebaseGoogleSignIn = () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const firebaseTwitterSignIn = () => {
    const provider = new firebase.auth.TwitterAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const firebaseFacebookSignIn = () => {
    const provider = new firebase.auth.FacebookAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const firebaseRegister = async (email: string, password: string) => firebase.auth().createUserWithEmailAndPassword(email, password);

  const logout = () => firebase.auth().signOut();

  const resetPassword = async (email: string) => {
    await firebase.auth().sendPasswordResetEmail(email);
  };

  const updateProfile = async () => {};

  const uploadProfilePicture = async (data: any): Promise<string | null> => {
    try {
      const storageRef = ref(storage, data.name);
      const uploadTask = uploadBytesResumable(storageRef, data);
      const snapshot = await uploadTask;

      const downloadedURL = await getDownloadURL(snapshot.ref);
      return downloadedURL;
    } catch (error) {
      console.error('Error uploading data: ', error);
      return null;
    }
  };

  if (state.isInitialized !== undefined && !state.isInitialized && !authServerError) {
    return <Loader />;
  }

  return (
    <FirebaseContext.Provider
      value={{
        ...state,
        firebaseRegister,
        firebaseEmailPasswordSignIn,
        login: () => {},
        firebaseGoogleSignIn,
        firebaseTwitterSignIn,
        firebaseFacebookSignIn,
        logout,
        resetPassword,
        updateProfile,
        uploadProfilePicture,
        authServerError
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebaseContext = () => {
  const context = useContext(FirebaseContext);
  if (!context) {
    throw new Error('useFirebase must be used within a FirebaseProvider');
  }
  return context;
};

export default FirebaseContext;
